import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/kfmApi";
import { Activity, Zap, Users, Coins, RefreshCw, Circle } from "lucide-react";

const WINDOWS = [
  ["hour", "Live · last hour"],
  ["day", "Last 24 hours"],
  ["week", "Last 7 days"],
];

const REASON_LABEL = {
  study_start: "New study",
  go_deeper: "Go Deeper chat",
  explain: "Simple explanation",
  cross_references: "Cross-references",
  prayer: "Prayer",
  study_to_sermon: "Study → Sermon",
  prayer_to_study: "Prayer → Study",
  one_to_one_to_study: "1:1 → Study",
  daily_social_post: "Social post",
  weekly_blog_post: "Blog post",
  scripture_graphic: "Scripture graphic",
};

const reasonLabel = (r) => REASON_LABEL[r] || (r || "Activity").replace(/_/g, " ");

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function StatBox({ icon: Icon, label, value }) {
  return (
    <div className="flex-1 min-w-[120px] rounded-2xl bg-[#0E131F] border border-[#1E273D] p-4">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 mb-2">
        <Icon className="w-3.5 h-3.5 text-[#00E5FF]" strokeWidth={1.5} /> {label}
      </div>
      <div className="text-3xl text-white">{value}</div>
    </div>
  );
}

const TIER_CLS = {
  free: "text-slate-400 bg-[#141B29]",
  basic: "text-sky-300 bg-sky-500/10",
  pro: "text-[#00E5FF] bg-[#10222A]",
  team: "text-amber-300 bg-amber-500/10",
};

export default function CreditUsage() {
  const [window, setWindow] = useState("hour");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const windowRef = useRef(window);
  windowRef.current = window;

  const load = useCallback(async (silent) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await api.get(`/admin/credits?window=${windowRef.current}`);
      setData(data);
      setError(false);
    } catch {
      setError(true);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load(false);
  }, [window, load]);

  useEffect(() => {
    const t = setInterval(() => load(true), 10000);
    return () => clearInterval(t);
  }, [load]);

  const totals = data?.totals || {};
  const feed = data?.feed || [];
  const topUsers = data?.top_users || [];

  return (
    <div data-testid="admin-credits" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2 p-1 bg-[#141B29] rounded-xl w-fit overflow-x-auto no-scrollbar">
          {WINDOWS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`credits-window-${id}`}
              onClick={() => setWindow(id)}
              className={`shrink-0 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                window === id ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Circle className={`w-2 h-2 ${refreshing ? "fill-emerald-400 text-emerald-400" : "fill-emerald-300 text-emerald-300"}`} />
          <span>Auto-refreshing</span>
          <button
            data-testid="credits-refresh"
            onClick={() => load(true)}
            className="ml-1 p-1.5 rounded-lg hover:bg-[#141B29] text-slate-500 hover:text-[#00E5FF] transition-colors"
            title="Refresh now"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {error && (
        <p data-testid="credits-error" className="text-sm text-slate-500 py-4 text-center">
          {"Couldn't load usage right now."}
        </p>
      )}

      <div className="flex gap-3 flex-wrap">
        <StatBox icon={Coins} label="Credits used" value={loading ? "…" : totals.credits ?? 0} />
        <StatBox icon={Activity} label="AI actions" value={loading ? "…" : totals.actions ?? 0} />
        <StatBox icon={Users} label="Active users" value={loading ? "…" : totals.users ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top users */}
        <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5" data-testid="credits-top-users">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500 mb-4">
            <Zap className="w-3.5 h-3.5 text-[#00E5FF]" strokeWidth={1.5} /> Top users this window
          </div>
          {topUsers.length === 0 && !loading ? (
            <p className="text-sm text-slate-500 py-6 text-center">No usage yet in this window.</p>
          ) : (
            <div className="space-y-1">
              {topUsers.slice(0, 15).map((u) => (
                <div key={u.user_id} data-testid={`credits-user-${u.user_id}`} className="flex items-center justify-between gap-3 py-2 border-b border-[#1E273D] last:border-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-200 truncate">{u.email}</span>
                      {u.tier && (
                        <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${TIER_CLS[u.tier] || TIER_CLS.free}`}>{u.tier}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {u.actions} action{u.actions === 1 ? "" : "s"}
                      {u.balance != null && <span> · {u.balance} credits left</span>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-lg text-white">{u.credits}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">used</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Live feed */}
        <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5" data-testid="credits-feed">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500 mb-4">
            <Activity className="w-3.5 h-3.5 text-[#00E5FF]" strokeWidth={1.5} /> Live activity
          </div>
          {feed.length === 0 && !loading ? (
            <p className="text-sm text-slate-500 py-6 text-center">No activity yet in this window.</p>
          ) : (
            <div className="space-y-1 max-h-[420px] overflow-y-auto">
              {feed.map((f, i) => (
                <div key={i} data-testid="credits-feed-row" className="flex items-center justify-between gap-3 py-2 border-b border-[#1E273D] last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200 truncate">{reasonLabel(f.reason)}</div>
                    <div className="text-xs text-slate-500 truncate">{f.email}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    {f.cost > 0 ? (
                      <span className="text-sm font-medium text-[#00E5FF]">−{f.cost}</span>
                    ) : (
                      <span className="text-xs text-slate-500">free</span>
                    )}
                    <div className="text-[11px] text-slate-500">{timeAgo(f.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
